import React, { useEffect, useState } from "react";
import { errorLogger } from "@/Common/helpers/logger";
import { updateSystemSettings } from "@/Settings/api/SystemSettings/mutations";
import { Grid } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { IUser } from "@/Common/interfaces/user";
import isEqual from "lodash/isEqual";
import MultipleSelectGroup from "@/Layouts/components/MultipleSelectGroup/MultipleSelectGroup";

type TCommissionSupportProps = {
	disabled: boolean;
	contacts?: IUser[];
	locationId: string;
};

const compareAlphabetically = (a: string, b: string) => {
	return a.localeCompare(b);
};

const CommissionSupport = React.memo((props: TCommissionSupportProps) => {
	const { locationId, disabled, contacts = [] } = props;
	const [internalContacts, setInternalContacts] = useState<string[]>([]);

	const queryClient = useQueryClient();

	const { getAccountingMembers: possibleContacts = [] } = queryClient.getQueryData(["systemSettingsAdditionalInfo", locationId]) as {
		getAccountingMembers: { _id: string; label: string }[];
	};

	const { mutate, isPending } = useMutation({
		mutationFn: (input: string[]) => updateSystemSettings(locationId, { commissionSupportTeam: input }, "Commission Support Team"),
		onError: (error) => errorLogger(error),
	});

	const handleUpdate = (value: { _id: string; label: string }, position: number) => {
		const newContacts = [...internalContacts];
		newContacts[position] = value._id;
		setInternalContacts(newContacts);
		const oldContacts = contacts.map((contact) => contact._id ?? "");
		if (newContacts.every(Boolean) && !isEqual(newContacts.sort(compareAlphabetically), oldContacts.sort(compareAlphabetically))) {
			mutate(newContacts);
		}
	};

	const handleAdd = () => {
		const newContacts = [...internalContacts];
		newContacts.push("");
		setInternalContacts(newContacts);
	};

	const handleDelete = (position: number) => {
		const newContacts = internalContacts.filter((_, index) => index !== position);
		setInternalContacts(newContacts);
		const oldContacts = contacts.map((contact) => contact._id ?? "");
		if (newContacts.every(Boolean) && !isEqual(newContacts.sort(compareAlphabetically), oldContacts.sort(compareAlphabetically))) {
			mutate(newContacts);
		}
	};

	useEffect(() => {
		const ids = contacts.map((contact) => contact._id).filter(Boolean);
		setInternalContacts(ids as string[]);
		return () => {
			setInternalContacts([]);
		};
	}, [locationId, contacts]);

	return (
		<Grid container direction="row" className="!mt-[16px]" alignItems="flex-start">
			<Grid item className="position-mapping-payroll-column">
				<MultipleSelectGroup
					allowDeleteFirst
					disableClearable
					showDeleteIcon
					showErrorMessage
					selectBy={"_id"}
					showBy={"label"}
					orderBy={"label"}
					errorMessage={"Please select at least one correspondence"}
					identifier={"technicalContacts"}
					items={internalContacts.map((item) => {
						const label = possibleContacts.find((user) => user._id === item)?.label;
						if (label) {
							return { _id: item, label };
						}
						return { _id: item, label: "" };
					})}
					selectableOptions={possibleContacts.filter((item) => !internalContacts.includes(item._id))}
					placeholder={"Select an Option"}
					onChange={handleUpdate}
					onDelete={handleDelete}
					onAdd={handleAdd}
					disabled={disabled || isPending}
					disableAddButton={isPending || internalContacts.some((item) => !item)}
					errorsList={internalContacts.map((item) => !item)}
					hideTooltip={true}
					sxSelect={{ minWidth: 216 }}
				/>
			</Grid>
		</Grid>
	);
});

export default CommissionSupport;
